import type { ComponentProps, ReactNode } from "react";
import { useEffect, useRef, useState } from "react";

import { Button } from "@beweegbaas/ui/components/ui/button";
import { Dialog, DialogContent } from "@beweegbaas/ui/components/ui/dialog";
import { cn } from "@beweegbaas/ui/lib/utils";

export interface ImageObject {
  url: string;
  name: string;
}

interface LightboxGalleryItemProps extends Omit<ComponentProps<typeof Button>, "children"> {
  image: ImageObject;
  children?: (image: ImageObject) => ReactNode;
  onPress?: () => void;
}

export function LightboxGalleryItem({
  image,
  children,
  className,
  onPress,
  ...props
}: LightboxGalleryItemProps) {
  if (children) {
    return <>{children(image)}</>;
  }

  return (
    <Button
      variant="outline"
      className={cn("group relative h-auto cursor-pointer rounded-lg p-1", className)}
      onClick={onPress}
      {...props}
    >
      <img src={image.url} alt={image.name} className="size-16 rounded-lg object-cover" />
    </Button>
  );
}

interface LightboxGalleryProps extends Omit<ComponentProps<"div">, "children"> {
  images: ImageObject[];
  children?: (props: { images: ImageObject[] }) => ReactNode;
}

export function LightboxGallery({ images, children, className, ...props }: LightboxGalleryProps) {
  const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const selectedImageRef = useRef<HTMLDivElement>(null);

  const openModal = (index: number) => {
    setSelectedImageIndex(index);
    setIsOpen(true);
  };

  // Scroll to selected image when dialog opens
  useEffect(() => {
    if (isOpen && selectedImageIndex !== null && selectedImageRef.current) {
      selectedImageRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [isOpen, selectedImageIndex]);

  // If children is provided, use render props pattern
  if (children) {
    return <>{children({ images })}</>;
  }

  if (images.length === 0) {
    return null;
  }

  // Default behavior - render LightboxGalleryItem components in a grid
  return (
    <>
      <div className={cn("flex flex-wrap gap-2", className)} {...props}>
        {images.map((image, index) => (
          <LightboxGalleryItem
            key={`${image.url}-${index}`}
            image={image}
            onClick={() => openModal(index)}
          />
        ))}
      </div>

      {/* Dialog */}
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent
          className="max-h-[90vh] max-w-[90vw] border-none bg-black/80 p-0
            [&_[data-slot=dialog-close]]:text-white
            [&_[data-slot=dialog-close]]:hover:bg-white/10"
          overlayClassName="bg-black/80"
        >
          <div className="flex h-full flex-col gap-10 overflow-y-auto p-4">
            {images.map((image, index) => (
              <div
                key={`${image.url}-${index}`}
                ref={index === selectedImageIndex ? selectedImageRef : null}
                className="relative self-center"
              >
                <img
                  src={image.url}
                  alt={image.name}
                  className="max-h-[80vh] w-auto max-w-full self-center
                    rounded-lg object-contain shadow-lg"
                />
                <span
                  className="absolute right-0 bottom-0 inline-flex
                    rounded-tl-lg rounded-br-lg bg-linear-to-br from-black/20
                    to-black/50 px-2 py-1 text-xs text-white"
                >
                  {image.name}
                </span>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
